import {FileUploadWithModal} from '@/components/organisms'
import {Constants} from '@/utils/constants/commonConstants'
import {useIntl} from 'react-intl'
import {convertCSV} from '@/helpers/convertCSV'
import {postUploadStores} from '@/services/ClubPlus/TierService'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {useAppDispatch} from '@/store'
import {useMutation} from '@tanstack/react-query'
import {useState, useEffect} from 'react'

const progressInterval = 200
const simulatedProgressMax = 90
const simulatedProgressIncrement = 5

export const TierFileUploadModal = ({isVisible, setIsVisible, onSuccess, setFormData, formData, tierID, tierName}) => {
  const {formatMessage} = useIntl()
  const dispatch = useAppDispatch()
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [parsedStoreIds, setParsedStoreIds] = useState([])

  const {mutateAsync, isPending} = useMutation({
    mutationKey: ['postUploadStores'],
    mutationFn: payload => postUploadStores(payload),
  })

  useEffect(() => {
    if (!isVisible) {
      setUploadProgress(0)
      setUploading(false)
      setParsedStoreIds([])
    }
  }, [isVisible])

  const closeModal = () => {
    setIsVisible(false)
    setFormData({})
    setUploadProgress(0)
    setUploading(false)
    setParsedStoreIds([])
  }

  const simulateProgress = () => {
    setUploadProgress(0)
    const interval = setInterval(() => {
      setUploadProgress(currentProgress => {
        const newProgress = currentProgress + simulatedProgressIncrement
        if (newProgress >= simulatedProgressMax) {
          clearInterval(interval)
          return simulatedProgressMax
        }
        return newProgress
      })
    }, progressInterval)

    return () => clearInterval(interval)
  }

  const handleUploadStores = async () => {
    const payload = {
      tierId: tierID,
      storeIds: parsedStoreIds,
    }

    setUploading(true)
    const stopProgress = simulateProgress()

    try {
      const response = await mutateAsync(payload)

      setUploadProgress(100)

      if (response?.unprocessedStoreIds?.length === 0) {
        onSuccess()
        dispatch(
          setToastNotifications({
            show: true,
            message: formatMessage({id: 'CLUB_PLUS.TIERS_ADD_STORES_UPLOAD_SUCCESS'}),
            type: 'success',
          })
        )
      } else if (response?.unprocessedStoreIds === null) {
        dispatch(
          setToastNotifications({
            show: true,
            message: formatMessage({id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_ERROR'}, {br: <br />}),
            type: 'error',
          })
        )
      } else {
        const conflictingStoresNumber = response?.unprocessedStoreIds?.length || 0
        const totalStoresNumber = parsedStoreIds.length
        const successStoresNumber = totalStoresNumber - conflictingStoresNumber

        dispatch(
          setToastNotifications({
            show: true,
            message: formatMessage(
              {id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_WARNING'},
              {tierName, conflictingStoresNumber, totalStoresNumber, successStoresNumber}
            ),
            type: 'warning',
          })
        )
      }
    } catch (error) {
      setUploadProgress(0)
      const errMsg = error?.cause || error?.message || formatMessage({id: 'GENERAL.UNKNOWN_ERROR'})
      dispatch(
        setToastNotifications({
          show: true,
          message: errMsg,
          type: 'error',
        })
      )
    } finally {
      stopProgress()
      setUploading(false)
      closeModal()
    }
  }

  const handleDeleteFile = () => {
    setParsedStoreIds([])
    setFormData({})
  }

  return (
    <FileUploadWithModal
      openUpload={isVisible}
      setOpenUpload={closeModal}
      formData={formData}
      setFormData={setFormData}
      title={formatMessage({id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_TITLE'})}
      label={formatMessage({id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_LABEL'}, {tierName})}
      uploadFor={Constants.RewardsTierStoreUpload}
      uploading={uploading}
      setUploading={setUploading}
      uploadProgress={uploadProgress}
      customUploadFile={async (file, handleFileError) => {
        const reader = new FileReader()
        reader.onload = async () => {
          try {
            const csvText = reader.result
            const convertedCSV = convertCSV(csvText)
            const newStoreIDs = convertedCSV
              .map(item => item['Store Id'])
              .filter(id => id && id.trim() !== '')

            if (newStoreIDs.length === 0) {
              handleFileError({
                feedbackType: 'error',
                helperText: formatMessage({id: 'CLUB_PLUS.TIER_FILE_UPLOAD_EMPTY_CSV'}),
              })
              setFormData({})
              return
            }

            const uniqueStoreIds = [...new Set(newStoreIDs)]
            if (uniqueStoreIds.length !== newStoreIDs.length) {
              handleFileError({
                feedbackType: 'warning',
                helperText: formatMessage({id: 'CLUB_PLUS.TIER_FILE_UPLOAD_MODAL_DIALOG_DUPLICATE_STORE_IDS'}),
              })
              setFormData({})
              return
            }

            setParsedStoreIds(uniqueStoreIds)
          } catch (error) {
            handleFileError({
              feedbackType: 'error',
              helperText: formatMessage({id: 'CLUB_PLUS.TIER_FILE_UPLOAD_INVALID_CSV'}),
            })
            setFormData({})
          }
        }

        reader.readAsText(file)
      }}
      onClickPrimary={handleUploadStores}
      onDelete={handleDeleteFile}
      primaryButtonDisabled={isPending || parsedStoreIds.length === 0}
      primaryButtonText={`Upload ${parsedStoreIds.length} store${parsedStoreIds.length !== 1 ? 's' : ''}`}
      required={false}
    />
  )
}
